"use client";

import { useEffect, useRef, useState } from "react";
import { saveDeferredProfileAnswers } from "@/actions/deferred-profile-answers";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { GENDER, type Gender } from "@/db/schema/profile";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import {
  type EventDeferredProfileAnswer,
  getGenderExclusionMessage,
} from "@/lib/event-access";
import { cn } from "@/lib/utils";

const GENDER_OPTION_LABEL: Record<Gender, string> = {
  female: "Female",
  male: "Male",
  other: "Other",
};

export type DeferredAnswersContext = {
  surface: string;
  hiiveSlug?: string;
  eventSlug?: string;
  occurrenceId?: number | null;
};

type DeferredAnswersDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which questions to ask, in order. Today only `gender` exists. */
  answers: EventDeferredProfileAnswer[];
  /** Genders this event admits, used to judge the answer and explain a refusal. */
  allowedGenders: Gender[];
  /** Every question answered and every answer acceptable — carry on. */
  onAnswered: () => void;
  /**
   * Answered, and the answer disqualifies them. The drawer says so itself; this
   * exists so the page behind it can stop claiming the viewer is bookable.
   */
  onRefused?: () => void;
  context: DeferredAnswersContext;
};

/**
 * The questions a purchase needs answered, asked at the point of purchase.
 *
 * ## Why this exists
 *
 * A gender-restricted event used to refuse anybody whose profile had no gender
 * on it, with a caption ("Add your gender to your profile to book this event.")
 * and no button. Every first-time booker is in exactly that state — checkout
 * sign-in deliberately creates an incomplete profile so buying does not require
 * onboarding — so the people the event was made for were the people it turned
 * away, and the only route through was to guess that the answer lived in
 * profile settings.
 *
 * ## Why the copy is generic
 *
 * The title does not mention gender. This is the slot for "facts we are missing
 * and can ask for", and the questions are driven by the `answers` list, so the
 * next one to be added needs no new drawer and no new copy. What must stay
 * specific is the refusal: `getGenderExclusionMessage` names the audience,
 * because "not eligible" after being asked a question reads as a bug.
 *
 * ## Not a gate
 *
 * `onAnswered` runs the caller's submit; the server re-reads the stored gender
 * and refuses a mismatch on its own. Skipping this drawer gets you refused at
 * the booking, not admitted.
 */
export function DeferredAnswersDrawer({
  open,
  onOpenChange,
  answers,
  allowedGenders,
  onAnswered,
  onRefused,
  context,
}: DeferredAnswersDrawerProps) {
  const [gender, setGender] = useState<Gender | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A saved answer that disqualifies them. Terminal: no retry to offer. */
  const [exclusion, setExclusion] = useState<string | null>(null);

  const asksGender = answers.includes("gender");

  /*
   * `answers` and `context` are rebuilt by the caller on every render, so they
   * cannot be dependencies of the effect below — the prompt event would fire
   * again on every keystroke elsewhere on the page. Opening the drawer is the
   * event, and this ref is what lets the effect say so with one dependency.
   */
  const promptPropertiesRef = useRef<Record<string, unknown>>({});
  promptPropertiesRef.current = { answers: answers.join(","), ...context };

  useEffect(() => {
    if (!open) return;
    setError(null);
    captureClientEvent(
      ANALYTICS_EVENTS.DEFERRED_PROFILE_ANSWER_PROMPTED,
      promptPropertiesRef.current,
    );
  }, [open]);

  const submit = async () => {
    if (!asksGender || !gender) {
      setError("Please select an option to continue.");
      return;
    }

    setPending(true);
    setError(null);
    const result = await saveDeferredProfileAnswers({ gender });
    setPending(false);

    if (!result.ok) {
      setError(result.message);
      return;
    }

    // Judge the STORED value, not the tap. They are the same on the normal
    // path, and differ in the one case that matters: a gender was already on
    // the profile, so the answer just given was not written and must not be
    // what decides eligibility.
    const stored = result.data.gender;
    const message = getGenderExclusionMessage(stored, allowedGenders);

    if (message) {
      captureClientEvent(ANALYTICS_EVENTS.DEFERRED_PROFILE_ANSWER_REFUSED, {
        answer: "gender",
        already_answered: result.data.alreadyAnswered,
        ...context,
      });
      setExclusion(message);
      onRefused?.();
      return;
    }

    captureClientEvent(ANALYTICS_EVENTS.DEFERRED_PROFILE_ANSWER_SAVED, {
      answer: "gender",
      already_answered: result.data.alreadyAnswered,
      ...context,
    });
    onOpenChange(false);
    onAnswered();
  };

  const title =
    answers.length > 1 ? "Answer these questions" : "Answer this question";

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (pending) return;
        onOpenChange(next);
      }}
    >
      <DrawerContent className="mx-auto max-h-[82dvh] max-w-[520px] rounded-t-[30px] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.14] dark:bg-[#090b10] dark:text-white">
        <DrawerHeader className="px-4 pb-2 text-left">
          <DrawerTitle className="text-2xl font-semibold text-pretty text-text-primary dark:text-white">
            {exclusion ? "You're not eligible for this event" : title}
          </DrawerTitle>
          <DrawerDescription className="text-sm text-text-secondary dark:text-white/60">
            {exclusion
              ? "Nothing has been charged and no ticket was created."
              : "We need this on your profile before you can book. It only takes a second."}
          </DrawerDescription>
        </DrawerHeader>

        <div className="space-y-3 overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom)+16px)]">
          {exclusion ? (
            <>
              <p className="rounded-[24px] border border-border bg-surface-alt px-4 py-4 text-sm text-text-secondary dark:border-glass-border-10 dark:bg-glass-bg-04 dark:text-white/72">
                {exclusion}
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full border-border bg-surface-alt text-text-primary hover:bg-surface-elevated dark:border-glass-border-12 dark:bg-glass-bg-04 dark:text-white dark:hover:bg-glass-bg-08"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
            </>
          ) : (
            <>
              {asksGender ? (
                <fieldset className="space-y-2">
                  <legend className="pb-2 text-sm font-medium text-text-primary dark:text-white">
                    What's your gender?
                  </legend>
                  <div className="overflow-hidden rounded-[24px] border border-border bg-surface-alt dark:border-glass-border-10 dark:bg-glass-bg-04">
                    {GENDER.map((option, index) => (
                      <label
                        key={option}
                        className={cn(
                          "flex cursor-pointer items-center gap-3 px-4 py-4 text-sm",
                          index > 0 &&
                            "border-t border-border dark:border-glass-border-10",
                          gender === option
                            ? "text-text-primary dark:text-white"
                            : "text-text-secondary dark:text-white/72",
                        )}
                      >
                        <input
                          type="radio"
                          name="deferred-gender"
                          value={option}
                          checked={gender === option}
                          onChange={() => {
                            setGender(option);
                            setError(null);
                          }}
                          className="h-4 w-4 accent-brand"
                        />
                        {GENDER_OPTION_LABEL[option]}
                      </label>
                    ))}
                  </div>
                </fieldset>
              ) : null}

              {error ? (
                <p className="px-1 text-sm text-destructive">{error}</p>
              ) : null}

              <Button
                type="button"
                className="w-full rounded-full"
                disabled={pending}
                onClick={() => {
                  void submit();
                }}
              >
                {pending ? "Saving…" : "Save and continue"}
              </Button>
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
